import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle,
  FileSignature, Lock, Zap, Flame, Wifi, Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pagina pubblica firma contratto - accesso via link `/firma/:token`.
 * Nessuna autenticazione richiesta - la sicurezza e' basata su token opaco + OTP.
 */
const PublicFirma = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(null); // { firma_hash, firmato_at }
  const [form, setForm] = useState({
    nome: '',
    cognome: '',
    codice_fiscale: '',
    otp: '',
    consenso_gdpr: false,
    consenso_condizioni: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/firma/${token}`);
        setInfo(res.data);
        // Pre-fill dai dati anagrafica (il cliente conferma solo che sono i suoi)
        if (res.data?.cliente) {
          setForm(prev => ({
            ...prev,
            nome: res.data.cliente.nome || prev.nome,
            cognome: res.data.cliente.cognome || prev.cognome,
            codice_fiscale: (res.data.cliente.codice_fiscale || prev.codice_fiscale || '').toUpperCase(),
          }));
        }
      } catch (e) {
        setInfo({ error: e.response?.data?.detail || 'Richiesta non trovata' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.cognome.trim()) return toast.error('Nome e cognome obbligatori');
    if (!/^\d{6}$/.test(form.otp)) return toast.error('Il codice OTP deve essere di 6 cifre');
    if (!form.consenso_gdpr || !form.consenso_condizioni) return toast.error('Accetta i consensi per firmare');

    setSigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/firma/${token}/verifica`, form);
      setSigned(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore firma');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (info?.error) return <FirmaError message={info.error} />;
  if (info?.status === 'scaduto') return <FirmaError message="Il link di firma e' scaduto. Contatta la tua agenzia per richiederne uno nuovo." />;
  if (info?.status === 'annullato') return <FirmaError message="La richiesta di firma e' stata annullata." />;

  // Success screen
  if (signed || info?.status === 'firmato') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-sky-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Contratto Firmato</h1>
            <p className="text-slate-600 text-sm">
              La firma è stata registrata correttamente. Riceverai a breve copia del contratto via email.
            </p>
            {signed?.firma_hash && (
              <div className="mt-6 pt-6 border-t border-slate-100 text-left">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Codice firma</p>
                <p className="font-mono text-xs text-slate-600 break-all">{signed.firma_hash}</p>
                <p className="text-xs text-slate-400 mt-3">
                  Firmato il {new Date(signed.firmato_at).toLocaleString('it-IT')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const TypeIcon = info.contract?.tipologia === 'gas' ? Flame : info.contract?.tipologia === 'internet' ? Wifi : Zap;

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header brand */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900">F.lli Schiliro' Servizi</span>
          </div>
          <p className="text-xs text-slate-500">Sottoscrizione contratto</p>
        </div>

        {/* Contract summary */}
        <Card className="mb-4" data-testid="firma-contratto-summary">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TypeIcon className="w-5 h-5 text-sky-500" />
              Contratto {(info.contract?.tipologia || '').toUpperCase()}
              {info.contract?.codice_pratica_interno && (
                <span className="ml-auto font-mono text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded-md">
                  {info.contract.codice_pratica_interno}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Cliente" value={info.cliente_label} />
            {info.contract?.fornitore && <Row label="Fornitore" value={info.contract.fornitore} />}
            {info.contract?.offerta && <Row label="Offerta" value={info.contract.offerta} />}
            {info.contract?.pod && <Row label="POD" value={info.contract.pod} mono />}
            {info.contract?.pdr && <Row label="PDR" value={info.contract.pdr} mono />}
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSignature className="w-5 h-5 text-sky-500" />
              Verifica i tuoi dati e firma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="firma-form">
              <p className="text-xs text-slate-500 -mt-1 flex items-start gap-1">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500" />
                I tuoi dati sono precompilati dall'anagrafica. Controlla che siano corretti e conferma con il codice OTP.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="nome" className="text-xs">Nome*</Label>
                  <Input
                    id="nome"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    required
                    data-testid="firma-nome"
                  />
                </div>
                <div>
                  <Label htmlFor="cognome" className="text-xs">Cognome*</Label>
                  <Input
                    id="cognome"
                    value={form.cognome}
                    onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                    required
                    data-testid="firma-cognome"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cf" className="text-xs">Codice Fiscale (opzionale)</Label>
                <Input
                  id="cf"
                  value={form.codice_fiscale}
                  onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
                  maxLength={16}
                  className="font-mono uppercase"
                  data-testid="firma-cf"
                />
              </div>

              {/* Consensi */}
              <div className="space-y-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Consensi</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.consenso_gdpr}
                    onCheckedChange={(v) => setForm({ ...form, consenso_gdpr: !!v })}
                    className="mt-0.5"
                    data-testid="firma-consenso-gdpr"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    <strong>Consenso trattamento dati (GDPR)</strong> — Acconsento al trattamento dei miei dati personali
                    per la gestione della fornitura come da informativa privacy.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.consenso_condizioni}
                    onCheckedChange={(v) => setForm({ ...form, consenso_condizioni: !!v })}
                    className="mt-0.5"
                    data-testid="firma-consenso-condizioni"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    <strong>Condizioni contrattuali</strong> — Dichiaro di aver ricevuto e letto le condizioni generali di
                    fornitura, il preventivo economico e di accettarli integralmente.
                  </span>
                </label>
              </div>

              {/* OTP */}
              <div className="p-4 bg-sky-50 rounded-xl border border-sky-200">
                <Label htmlFor="otp" className="text-xs font-semibold text-sky-800 flex items-center gap-1 mb-2">
                  <Lock className="w-3.5 h-3.5" /> Codice OTP ricevuto via email
                </Label>
                <Input
                  id="otp"
                  value={form.otp}
                  onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="123456"
                  className="text-center font-mono text-2xl tracking-widest h-12 bg-white"
                  maxLength={6}
                  inputMode="numeric"
                  required
                  data-testid="firma-otp"
                />
                <p className="text-[11px] text-sky-700 mt-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Il codice è valido 30 minuti. Controlla anche la cartella spam.
                </p>
              </div>

              <Button
                type="submit"
                disabled={signing}
                className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold"
                data-testid="firma-submit"
              >
                {signing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firma in corso…</>
                ) : (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Conferma e firma</>
                )}
              </Button>

              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                Facendo clic su "Conferma e firma" viene registrata la sottoscrizione con audit trail
                (data, ora, indirizzo IP, hash dei dati). La firma OTP ha valore legale ai sensi del
                Regolamento eIDAS UE 910/2014 come firma elettronica semplice.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-500 w-24 shrink-0">{label}</span>
    <span className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
  </div>
);

const FirmaError = ({ message }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <Card className="w-full max-w-md">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
          <AlertTriangle className="w-9 h-9 text-red-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Richiesta non valida</h1>
        <p className="text-slate-600 text-sm">{message}</p>
      </CardContent>
    </Card>
  </div>
);

export default PublicFirma;
